'use client';

import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast';

/**
 * Banner shown above main content when the signed-in user hasn't verified
 * their email yet. Posting public prompts is gated server-side; this banner
 * just nudges them + offers a one-click resend (rate-limited to 1/2min).
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const toast = useToast();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified_at) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.auth.requestVerify();
      setSent(true);
      toast.success('Verification email sent. Check your inbox.');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Couldn’t resend.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px',
        background: 'rgba(245, 158, 11, 0.10)',
        borderBottom: '1px solid rgba(245, 158, 11, 0.30)',
        color: 'var(--ink)',
        fontSize: 13,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#f59e0b' }}>
        mark_email_unread
      </span>
      <div style={{ flex: 1 }}>
        <b>Confirm your email</b> to post public prompts. Private drafts work without verification.
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={busy || sent}
        className="ps-btn ps-btn-ghost ps-btn-sm"
        style={{ flexShrink: 0 }}
      >
        {sent ? 'Sent ✓' : busy ? 'Sending…' : 'Resend email'}
      </button>
    </div>
  );
}
